import { FoodsIconButton, SearchBox, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import type { ReactNode } from "react";

import { FoodsBottomSheetDialog } from "../Dialog/FoodsBottomSheetDialog";

type ReferenceSearchDialogProps = {
    children: ReactNode;
    closeLabel?: string;
    footer?: ReactNode;
    footerHeight?: number;
    maxHeight?: number;
    onClose: () => void;
    onSearchChange: (value: string) => void;
    open: boolean;
    searchPlaceholder: string;
    searchValue: string;
    title: string;
    toolbar?: ReactNode;
};

export const ReferenceSearchDialog = (props: ReferenceSearchDialogProps) => {
    const {
        children,
        closeLabel = "Close",
        footer,
        footerHeight = 76,
        maxHeight = 672,
        onClose,
        onSearchChange,
        open,
        searchPlaceholder,
        searchValue,
        title,
        toolbar,
    } = props;
    const theme = useTheme();
    const header = (
        <Box
            component="header"
            data-testid="reference-search-header"
            sx={{
                alignItems: "center",
                boxSizing: "border-box",
                display: "flex",
                height: theme.spacing(9.5),
                justifyContent: "space-between",
                minWidth: 0,
                padding: theme.spacing(2),
                width: "100%",
            }}
        >
            <Typography
                component="span"
                id="reference-search-title"
                variant="body-bold"
                sx={{
                    color: theme.custom.grey[2800],
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {title}
            </Typography>
            <FoodsIconButton
                icon="close"
                size="m"
                buttonProps={{
                    "aria-label": closeLabel,
                    onClick: onClose,
                    type: "button",
                }}
            />
        </Box>
    );

    const content = (
        <Stack
            data-testid="reference-search-body"
            minHeight={0}
            sx={{
                maxHeight: `min(${maxHeight - 76}px, calc(100dvh - ${theme.spacing(13.5)}))`,
                overflow: "hidden",
            }}
        >
            <Box
                data-testid="reference-search-content"
                sx={{
                    boxSizing: "border-box",
                    display: "flex",
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflow: "hidden",
                }}
            >
                <Stack flex="1 1 auto" gap={theme.spacing(2)} minHeight={0} paddingTop={theme.spacing(2)}>
                    {toolbar && (
                        <Stack flexShrink={0} height={theme.spacing(5)} paddingX={theme.spacing(2)}>
                            {toolbar}
                        </Stack>
                    )}
                    <Stack flexShrink={0} height={theme.spacing(5)} paddingX={theme.spacing(2)}>
                        <SearchBox
                            onChange={onSearchChange}
                            placeholder={searchPlaceholder}
                            value={searchValue}
                            variant="standalone"
                        />
                    </Stack>
                    {children}
                </Stack>
            </Box>
            {footer !== undefined && (
                <Box
                    component="footer"
                    data-testid="reference-search-footer"
                    sx={{
                        boxSizing: "border-box",
                        flexShrink: 0,
                        height: `${footerHeight}px`,
                        overflow: "hidden",
                    }}
                >
                    {footer}
                </Box>
            )}
        </Stack>
    );

    return (
        <FoodsBottomSheetDialog
            aria-labelledby="reference-search-title"
            content={content}
            onClose={onClose}
            open={open}
            PaperProps={{
                "aria-label": title,
                sx: {
                    borderRadius: theme.spacing(1.5),
                    height: "auto",
                    margin: theme.spacing(2),
                    maxHeight: `min(${maxHeight}px, calc(100dvh - ${theme.spacing(4)}))`,
                    maxWidth: theme.spacing(92),
                    overflow: "hidden",
                    width: `calc(100% - ${theme.spacing(4)})`,
                    // BottomSheetDialog switches every sub-md dialog to a full-width,
                    // bottom-pinned sheet. Reference-search designs remain centered,
                    // responsive modal surfaces (Figma 12803:67915 / 12906:84824).
                    "&&&": {
                        bottom: "auto",
                        borderRadius: theme.spacing(1.5),
                        left: "auto",
                        margin: theme.spacing(2),
                        maxHeight: `min(${maxHeight}px, calc(100dvh - ${theme.spacing(4)}))`,
                        maxWidth: theme.spacing(92),
                        position: "relative",
                        right: "auto",
                        width: `calc(100% - ${theme.spacing(4)})`,
                    },
                    "& > .MuiDialogTitle-root + .MuiBox-root": {
                        maxHeight: "none",
                        overflowY: "hidden",
                    },
                    "& .MuiDialogTitle-root": {
                        borderBottom: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                        boxSizing: "border-box",
                        height: theme.spacing(9.5),
                        minHeight: theme.spacing(9.5),
                        padding: 0,
                    },
                    "& .MuiDialogTitle-root > .MuiButton-root": {
                        display: "none",
                    },
                },
            }}
            setOpen={() => undefined}
            title={header}
        />
    );
};
